import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Ellipsis, GripVertical, Plus } from 'lucide-react';
import { PAGE_LIMIT, PAGE_NAME_MAX, type PageEntry, type SceneStore } from '@os/doc';
import { Button, cn, Dialog, Input, ListItem, Menu, Panel } from '../../ui';
import { Icon } from '../../ui/icons';
import { menuTriggerClass } from '../dashboard/ItemMenu';
import { pageLabel } from './labels';
import type { PageNav } from './page-nav';
import { PageThumb } from './PageThumb';

/** `onDeleted`: a page was deleted here; the editor shows the toast with Undo, so it outlives the panel (P15c). */
type Props = { store: SceneStore; nav: PageNav; onClose: () => void; onDeleted: () => void; toggle?: RefObject<HTMLElement | null> };

/** Re-renders on the page list, the active page, the objects (counts) and read-only changes. */
function usePagesView(store: SceneStore) {
  const pages = useSyncExternalStore(useCallback((cb: () => void) => store.subscribePages(cb), [store]), () => store.pages());
  const page = useSyncExternalStore(useCallback((cb: () => void) => store.subscribePage(cb), [store]), () => store.page);
  // Counts change only when objects come, go or change page: not on every drag frame.
  const [, setMembership] = useState(0);
  useEffect(() => store.subscribeMembership(() => setMembership((n) => n + 1)), [store]);
  const readOnly = useSyncExternalStore(useCallback((cb: () => void) => store.subscribeReadOnly(cb), [store]), () => store.readOnly);
  return { pages, page, readOnly };
}

/**
 * The board's pages (P14 backlog §16.2): a vignette, the name (« Page N » when unnamed) and the object count of each,
 * in order. A row opens its page; editors add, rename (double-click, F2), duplicate, delete (confirmation, then
 * « Undo ») and reorder (drag by the grip, Alt+↑/↓). Readers and commenters only navigate. Docked like History,
 * full width on a phone.
 */
export function PagesPanel({ store, nav, onClose, onDeleted, toggle }: Props) {
  const { t } = useTranslation();
  const { pages, page, readOnly } = usePagesView(store);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<PageEntry | null>(null);
  const list = useRef<HTMLOListElement>(null);
  const drag = useRef<{ id: string; to: number } | null>(null);
  const [dragTo, setDragTo] = useState<number | null>(null);
  const label = (p: PageEntry) => pageLabel(t, p, pages.indexOf(p));
  const full = pages.length >= PAGE_LIMIT;

  const focusRow = (id: string) => requestAnimationFrame(() => list.current?.querySelector<HTMLElement>(`[data-page-open="${id}"]`)?.focus());
  const add = () => {
    const id = store.commands.addPage({ after: page });
    if (id) nav.go(id);
  };
  const duplicate = (p: PageEntry) => {
    const id = store.commands.duplicatePage(p.id, t('pages.copyOf', { name: label(p) }));
    if (id) focusRow(id);
  };
  const remove = (p: PageEntry) => {
    setConfirming(null);
    if (store.commands.deletePage(p.id)) onDeleted();
  };
  const onRowKey = (e: KeyboardEvent, p: PageEntry, index: number) => {
    if (readOnly) return;
    if (e.key === 'F2') {
      e.preventDefault();
      setRenaming(p.id);
    } else if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      const to = index + (e.key === 'ArrowUp' ? -1 : 1);
      if (to < 0 || to >= pages.length) return;
      store.commands.movePage(p.id, to);
      focusRow(p.id);
    }
  };
  // Drag by the grip: the row under the pointer is the target index; one movePage on release.
  const rowIndexAt = (y: number) => {
    const rows = [...(list.current?.querySelectorAll<HTMLElement>('[data-page-row]') ?? [])];
    const i = rows.findIndex((r) => y < r.getBoundingClientRect().bottom);
    return i < 0 ? rows.length - 1 : i;
  };
  const onGripDown = (e: ReactPointerEvent, p: PageEntry) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { id: p.id, to: pages.indexOf(p) };
  };
  const onGripMove = (e: ReactPointerEvent) => {
    if (!drag.current) return;
    drag.current.to = rowIndexAt(e.clientY);
    setDragTo(drag.current.to);
  };
  const onGripUp = () => {
    const d = drag.current;
    drag.current = null;
    setDragTo(null);
    if (d && d.to !== pages.findIndex((x) => x.id === d.id)) store.commands.movePage(d.id, d.to);
  };

  return (
    <Panel title={t('pages.title')} closeLabel={t('pages.close')} onClose={onClose} toggle={toggle} placement="right" className="top-24 bottom-20 gap-3 max-md:inset-x-3 max-md:w-auto">
      {!readOnly && (
        <div className="flex flex-col gap-1">
          <Button variant="secondary" onClick={add} disabled={full}>
            <Icon icon={Plus} size={16} />
            {t('pages.new')}
          </Button>
          {full && <p className="font-sans text-xs text-fg-muted">{t('pages.limit')}</p>}
        </div>
      )}
      <ol ref={list} className="flex flex-col gap-1 overflow-auto" aria-describedby={readOnly ? undefined : 'pages-reorder-hint'}>
        {pages.map((p, index) => {
          const name = label(p);
          const count = store.listPage(p.id).length;
          return (
            <li key={p.id} data-page-row={p.id} className={cn('flex items-center gap-1 rounded-lg', dragTo === index && 'outline outline-2 outline-accent')}>
              {!readOnly && (
                <span
                  aria-hidden="true"
                  title={t('pages.reorder')}
                  onPointerDown={(e) => onGripDown(e, p)}
                  onPointerMove={onGripMove}
                  onPointerUp={onGripUp}
                  onPointerCancel={onGripUp}
                  className="grid w-5 shrink-0 cursor-grab touch-none place-items-center text-fg-muted pointer-coarse:w-8"
                >
                  <Icon icon={GripVertical} size={14} />
                </span>
              )}
              {renaming === p.id ? (
                <RenameField
                  initial={p.name}
                  onDone={(next) => {
                    setRenaming(null);
                    if (next !== null && next !== p.name) store.commands.renamePage(p.id, next);
                    focusRow(p.id);
                  }}
                />
              ) : (
                <ListItem
                  data-page-open={p.id}
                  aria-current={p.id === page ? 'page' : undefined}
                  selected={p.id === page}
                  onClick={() => nav.go(p.id)}
                  onDoubleClick={() => !readOnly && setRenaming(p.id)}
                  onKeyDown={(e) => onRowKey(e, p, index)}
                  className="h-auto min-h-11 min-w-0 flex-1 rounded-lg py-1.5 pointer-coarse:h-auto"
                  label={
                    <span className="flex min-w-0 items-center gap-2">
                      <PageThumb store={store} pageId={p.id} className="h-10 w-16 shrink-0 rounded border border-border" />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">{name}</span>
                        <span className="text-xs text-fg-muted">{t('pages.objects', { count })}</span>
                      </span>
                    </span>
                  }
                />
              )}
              {!readOnly && renaming !== p.id && (
                <Menu.Root>
                  <Menu.Trigger aria-label={t('pages.actions', { name })} className={menuTriggerClass}>
                    <Icon icon={Ellipsis} size={18} />
                  </Menu.Trigger>
                  <Menu.Popup label={t('pages.actions', { name })} align="end">
                    <Menu.Item onClick={() => setRenaming(p.id)}>{t('pages.renameAction')}</Menu.Item>
                    <Menu.Item disabled={full} onClick={() => duplicate(p)}>
                      {t('pages.duplicate')}
                    </Menu.Item>
                    {pages.length > 1 && (
                      <>
                        <Menu.Separator />
                        <Menu.Item onClick={() => setConfirming(p)}>{t('pages.delete')}</Menu.Item>
                      </>
                    )}
                  </Menu.Popup>
                </Menu.Root>
              )}
            </li>
          );
        })}
      </ol>
      {!readOnly && (
        <p id="pages-reorder-hint" className="font-sans text-xs text-fg-muted pointer-coarse:hidden">
          {t('pages.reorderHint')}
        </p>
      )}
      {confirming && (
        <Dialog
          title={t('pages.deleteTitle', { name: label(confirming) })}
          onClose={() => setConfirming(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirming(null)}>
                {t('pages.cancel')}
              </Button>
              <Button variant="danger" onClick={() => remove(confirming)}>
                {t('pages.deleteConfirm')}
              </Button>
            </>
          }
        >
          <p className="font-sans text-sm text-fg">{t('pages.deleteBody', { count: store.listPage(confirming.id).length })}</p>
        </Dialog>
      )}
    </Panel>
  );
}

/** The name field of a page being renamed: Enter or leaving it keeps the name, Escape cancels (null). */
function RenameField({ initial, onDone }: { initial: string; onDone: (name: string | null) => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  const done = useRef(false);
  const finish = (name: string | null) => {
    if (done.current) return;
    done.current = true;
    onDone(name);
  };
  return (
    <Input
      autoFocus
      aria-label={t('pages.rename')}
      value={value}
      maxLength={PAGE_NAME_MAX}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => finish(value.trim())}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          finish(value.trim());
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          finish(null);
        }
      }}
      className="min-w-0 flex-1"
    />
  );
}
